import { FormattedMessage } from 'react-intl';
import { PoolSelect, SelectOption, useFilter } from '@synergy/components';
import { useEffect, useState } from 'react';

import { OrderingContact } from '../../../features/orders/orders-api';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/**
 * The filter by person of the member's order lists.
 *
 * It offers the people who ordered, from the orders themselves. The contact
 * list behind the other filters is the shop admin's alone, and a contact who
 * may see the company's orders without being one was sent to the login page
 * the moment the filter opened (#2328 R-248).
 */
export const OrderContactFilter = () => {
    const dispatch = useThunkDispatch();
    const { filter, getFromCache, updateCache, updateFilter, registerFilterMap } = useFilter();
    const [contacts, setContacts] = useState<OrderingContact[]>([]);

    useEffect(() => {
        void dispatch(async (_dispatch, _getState, { api }) => {
            const response = await api.orders.getCustomerContacts();

            if (response.success && response.result) {
                setContacts(response.result);
            }
        });
    }, [dispatch]);

    const onSelected = (contactId?: string) => {
        filter.delete('customerContactId');

        const contact = contacts.find(candidate => String(candidate.id) === contactId);

        if (contact) {
            updateCache(`customerContact_${contact.id}`, contact.fullName);
            filter.set('customerContactId', String(contact.id));
        }
        updateFilter(filter);
    };

    const getDisplayValue = (contactId: unknown): string => {
        if (typeof contactId !== 'string') {
            return '';
        }

        return (getFromCache(`customerContact_${contactId}`) as string) ?? '';
    };

    registerFilterMap('customerContactId', getDisplayValue);

    const options = contacts.map<SelectOption>(contact => ({
        value: String(contact.id),
        displayValue: contact.fullName,
    }));

    return (
        <PoolSelect
            displayEmpty={true}
            name="customerContactId"
            label={<FormattedMessage id="app.filter.customerContact" />}
            fullWidth={true}
            value={filter.get('customerContactId') ?? undefined}
            onValueChange={onSelected}
            options={options}
        />
    );
};
